import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, MarkerClusterer, Marker, InfoWindow, OverlayView } from '@react-google-maps/api';
import axios from 'axios';
import { CommanServices } from '@/services/commanServices';
import { toast } from 'sonner';
import { reportFeedEvent, distanceLabel, warningToEvent } from '@/components/externalEvents';
import EventPreviewModal from '@/components/EventPreviewModal';
import { ApiEndPoint } from '@/services/apiUttiLities';
import socketService from '@/services/socketService';
import MapMarker from '@/components/map/MapMarker';
import { buildZoneIndex, resolveIncidentCoords } from '@/components/map/zoneCoords';
import { MAP_LIBRARIES, GOOGLE_MAPS_LOADER_OPTIONS, useMapGis, usePersistentGisMap, GisSaveControl, MapGisPanel, MapGisOverlay, TaskFormModal, PointFormModal } from '@/components/map/MapGisTools';
import { useMapsUnavailable, MapUnavailable } from '@/components/map/MapUnavailable';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  Filter,
  Globe,
  Layers,
  Loader2,
  Map as MapIcon,
  Maximize2,
  Minimize2,
  Moon,
  Mountain,
  RefreshCw,
  Satellite,
  Search,
  SlidersHorizontal,
  Sun,
  Users,
} from 'lucide-react';
import MapEntityCard, {
  AssignedIncidentAction,
  buildDepartmentNameMap,
  getDepartmentDisplay,
  isUserLive,
  resolveEntityType,
} from '@/components/map/MapEntityCard';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { readStoredUser } from '@/auth/saasSession';
import { areForesightArtifactsEnabled } from '@/auth/featureEntitlements';
import { installOperationsAreaOverlay } from '@/components/map/operationsAreaOverlay';
import {
  loadOperationsAreaDisplay,
  resetOperationsAreaDisplay,
  saveOperationsAreaDisplay,
} from '../components/map/operationsAreaDisplayPreferences';

const containerStyle = { width: '100%', height: '100%' };
const fallbackCenter = { lat: 24.7136, lng: 46.6753 }; // used only until Operations Area loads
const USER_STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Users' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
];

const DUTY_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'in_duty', label: 'On duty' },
  { value: 'not_in_duty', label: 'Off duty' },
  { value: 'no_response', label: 'No response' },
];

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'real', label: 'Incidents' },
  { value: 'drill', label: 'Drills' },
];

// Public operational lifecycle statuses for real incidents and drills.
const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  requested: 'Requested',
  active: 'Active',
  completed: 'Completed',
  resolved: 'Resolved',
};
const statusLabel = (s) => STATUS_LABELS[String(s || '').toLowerCase()]
  || String(s || '—').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const MAP_VIEW_SESSION_KEY = 'mapPage_viewType';

const SEVERITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const MAP_VIEW_OPTIONS = [
  { id: 'dark', label: 'Dark', icon: Moon },
  { id: 'light', label: 'Light', icon: Sun },
  { id: 'plain', label: 'Plain', icon: MapIcon },
  { id: 'satellite', label: 'Satellite', icon: Satellite },
  { id: 'hybrid', label: 'Hybrid', icon: Layers },
  { id: 'terrain', label: 'Terrain', icon: Mountain },
];

const asFiniteCoordinate = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const coordinatePairOf = (record) => {
  const gps = record?.gps
    || record?.location?.gps
    || record?.location_resolution?.gps;
  const latitude = asFiniteCoordinate(gps?.latitude ?? gps?.lat);
  const longitude = asFiniteCoordinate(gps?.longitude ?? gps?.lng ?? gps?.lon);
  if (
    latitude === null
    || longitude === null
    || latitude < -90
    || latitude > 90
    || longitude < -180
    || longitude > 180
  ) {
    return null;
  }
  return { latitude, longitude };
};

/**
 * Turn the authoritative ForeSight decision-queue response into map markers.
 *
 * The API may be returned directly by FastAPI or wrapped by an older gateway.
 * Queue items are already tenant-scoped and watch-eligible on the server, so
 * the map only removes records whose event coordinates remain unresolved.
 * Risk-assessment fields deliberately play no part in map visibility.
 */
export const normalizeForesightMapWarnings = (response) => {
  const payload = response?.data || response;
  const records = Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.warnings)
      ? payload.warnings
      : [];
  const mapped = new Map();

  records.forEach((record) => {
    const gps = coordinatePairOf(record);
    if (!gps) return;
    const id = record?.warning_id
      || record?.id
      || record?.external_id
      || record?.event_key;
    if (!id) return;
    const detectingSource = record?.detecting_source
      || record?.primary_source
      || record?.source
      || {};
    mapped.set(String(id), {
      ...record,
      id,
      warning_id: record?.warning_id || record?.id || id,
      gps,
      source_name: record?.source_name
        || detectingSource.publisher
        || detectingSource.display_name
        || detectingSource.name
        || record?.connection?.name
        || 'External source',
      source_category: record?.source_category
        || detectingSource.category
        || record?.connection?.category,
      connection_id: record?.connection_id
        || detectingSource.connection_id
        || record?.connection?.id,
    });
  });

  return [...mapped.values()];
};

export const isInternalForesightWarning = (warning) => {
  const scope = warning?.operational_scope ?? warning?.location?.operational_scope;
  const scopeCode = typeof scope === 'string' ? scope : scope?.code;
  if (String(scopeCode || '').trim().toLowerCase() === 'internal') return true;
  if (String(scopeCode || '').trim().toLowerCase() === 'external') return false;
  return warning?.internal === true || warning?.location?.internal === true;
};

export const foresightMapFocusTarget = (searchParams, warnings = []) => {
  const id = String(searchParams?.get?.('focusWarning') || '').trim();
  if (!id) return null;

  const warning = warnings.find((entry) => (
    String(entry?.id || entry?.warning_id || entry?.external_id || '') === id
  )) || null;
  const warningGps = coordinatePairOf(warning);
  const queryGps = coordinatePairOf({
    gps: {
      latitude: searchParams?.get?.('lat'),
      longitude: searchParams?.get?.('lng'),
    },
  });
  const gps = warningGps || queryGps;
  if (!gps) return null;

  return { id, warning, gps };
};

export const focusForesightWarningViewport = (map, target, zoom = 17) => {
  if (!map || !target?.gps) return false;
  const position = {
    lat: Number(target.gps.latitude),
    lng: Number(target.gps.longitude),
  };
  if (!Number.isFinite(position.lat) || !Number.isFinite(position.lng)) return false;
  if (typeof map.panTo === 'function') map.panTo(position);
  if (typeof map.setZoom === 'function') map.setZoom(zoom);
  return true;
};

const appendBoundaryCoordinates = (coordinates, points) => {
  if (!Array.isArray(coordinates)) return;
  if (
    coordinates.length >= 2
    && asFiniteCoordinate(coordinates[0]) !== null
    && asFiniteCoordinate(coordinates[1]) !== null
  ) {
    points.push({
      lat: Number(coordinates[1]),
      lng: Number(coordinates[0]),
    });
    return;
  }
  coordinates.forEach((entry) => appendBoundaryCoordinates(entry, points));
};

/**
 * Frame both the configured country geometry and its watch radius. Keeping
 * this independent from the overlay lets the map start in the correct place
 * even when an operator has chosen to hide one of the visual layers.
 */
export const focusOperationsAreaViewport = ({
  map,
  mapsApi,
  area,
  boundary,
  padding = 56,
}) => {
  if (!map || !area) return false;

  const latitude = asFiniteCoordinate(area.center?.latitude ?? area.center?.lat);
  const longitude = asFiniteCoordinate(area.center?.longitude ?? area.center?.lng);
  if (latitude === null || longitude === null) return false;

  const points = [];
  appendBoundaryCoordinates(
    boundary?.type === 'Feature' ? boundary.geometry?.coordinates : boundary?.coordinates,
    points,
  );

  const radiusKm = Math.max(0, asFiniteCoordinate(area.radius_km) || 0);
  if (radiusKm > 0) {
    const latitudeDelta = radiusKm / 110.574;
    const longitudeScale = Math.max(0.15, Math.cos((latitude * Math.PI) / 180));
    const longitudeDelta = radiusKm / (111.32 * longitudeScale);
    points.push(
      { lat: latitude - latitudeDelta, lng: longitude - longitudeDelta },
      { lat: latitude + latitudeDelta, lng: longitude + longitudeDelta },
    );
  }

  if (mapsApi?.LatLngBounds && points.length > 0 && typeof map.fitBounds === 'function') {
    const bounds = new mapsApi.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, padding);
    return true;
  }

  if (typeof map.panTo === 'function') map.panTo({ lat: latitude, lng: longitude });
  if (typeof map.setZoom === 'function') {
    const radiusZoom = radiusKm > 0
      ? Math.max(2, Math.min(14, Math.round(14 - Math.log2(Math.max(1, radiusKm / 2)))))
      : 6;
    map.setZoom(radiusZoom);
  }
  return true;
};

const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1d2c4d' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8ec3b9' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a3646' }] },
  { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ color: '#4b6878' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#64779e' }] },
  { featureType: 'administrative.province', elementType: 'geometry.stroke', stylers: [{ color: '#4b6878' }] },
  { featureType: 'landscape.man_made', elementType: 'geometry.stroke', stylers: [{ color: '#334e87' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#023e58' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#283d6a' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6f9ba5' }] },
  { featureType: 'poi', elementType: 'labels.text.stroke', stylers: [{ color: '#1d2c4d' }] },
  { featureType: 'poi.park', elementType: 'geometry.fill', stylers: [{ color: '#023e58' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#3C7680' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#304a7d' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#98a5be' }] },
  { featureType: 'road', elementType: 'labels.text.stroke', stylers: [{ color: '#1d2c4d' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2c6675' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#255763' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#b0d5ce' }] },
  { featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ color: '#023e58' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#98a5be' }] },
  { featureType: 'transit.line', elementType: 'geometry.fill', stylers: [{ color: '#283d6a' }] },
  { featureType: 'transit.station', elementType: 'geometry', stylers: [{ color: '#3a4762' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4e6d70' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const LIGHT_MAP_STYLES = [
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const normalizeSeverity = (severity) => {
  const s = (severity || '').toLowerCase();
  if (s === 'critical' || s === 'high') return 'high';
  if (s === 'moderate' || s === 'medium') return 'medium';
  if (s === 'minor' || s === 'low') return 'low';
  return s;
};

const buildMapOptions = (viewType) => {
  const base = { streetViewControl: false, mapTypeControl: false, fullscreenControl: false };
  switch (viewType) {
    case 'light':
      return { ...base, mapTypeId: 'roadmap', styles: LIGHT_MAP_STYLES };
    case 'plain':
      return { ...base, mapTypeId: 'roadmap', styles: [
        { elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ] };
    case 'satellite':
      return { ...base, mapTypeId: 'satellite', styles: [] };
    case 'hybrid':
      return { ...base, mapTypeId: 'hybrid', styles: [] };
    case 'terrain':
      return { ...base, mapTypeId: 'terrain', styles: [] };
    case 'dark':
    default:
      return { ...base, mapTypeId: 'roadmap', styles: DARK_MAP_STYLES };
  }
};

const getIncidentRelationId = (inc) =>
  inc?.parent_incident_id || inc?.related_incident_id || null;

const getRelationshipDashIcons = (highlighted = false) => {
  if (!window.google?.maps) return [];
  return [{
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: highlighted ? 0.95 : 0.55,
      scale: highlighted ? 4 : 3,
      strokeColor: highlighted ? '#f97316' : '#0284c7',
    },
    offset: '0',
    repeat: highlighted ? '12px' : '16px',
  }];
};

const getLocationKey = (loc) => {
  if (!loc) return '';
  if (loc.user_id) return `user:${String(loc.user_id)}`;
  return `marker:${loc.team_id || 'x'}:${loc.crew_id || 'x'}`;
};

const parseTimestamp = (ts) => {
  if (!ts) return Date.now();
  const n = typeof ts === 'number' ? ts : new Date(ts).getTime();
  return Number.isNaN(n) ? Date.now() : n;
};

const normalizeLocations = (list) => {
  const byKey = new Map();
  for (const item of list || []) {
    if (!item) continue;
    const lat = parseFloat(item.lat);
    const lng = parseFloat(item.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
    const key = getLocationKey(item);
    const ts = parseTimestamp(item.timestamp);
    const existing = byKey.get(key);
    if (!existing || ts >= parseTimestamp(existing.timestamp)) {
      byKey.set(key, { ...existing, ...item, lat, lng, timestamp: ts });
    }
  }
  return Array.from(byKey.values());
};

const getUserMarkerIcon = (highlighted = false, online = true) => {
  if (!window.google?.maps) return undefined;
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: highlighted ? '#f59e0b' : (online ? '#0284c7' : '#6b7280'),
    fillOpacity: online ? 1 : 0.8,
    strokeColor: highlighted ? '#fef3c7' : (online ? '#ffffff' : '#e5e7eb'),
    strokeWeight: highlighted ? 3 : 2,
    scale: highlighted ? 12 : (online ? 9 : 8),
  };
};

// Global search: matches incidents & drills by id, title, classification
// (type / event / trigger), status, severity and resolved zone name.
const matchIncidentIdQuery = (incident, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    incident.id, incident.title, incident.incident_type, incident.event,
    incident.trigger, incident.status, incident.severity,
    incident.location_name, incident.zone_name,
    incident.is_exercise ? 'drill exercise' : 'real incident',
  ].some((v) => String(v || '').toLowerCase().includes(q));
};

const matchUserQuery = (user, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return false;
  const name = (user.name || '').toLowerCase();
  const username = (user.username || '').toLowerCase();
  const email = (user.email || '').toLowerCase();
  const department = (user.departmentName || '').toLowerCase();
  return name.includes(q) || username.includes(q) || email.includes(q) || department.includes(q);
};

const IncidentIdSearch = ({ incidents, value, onChange, onSelect }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const results = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return [];
    return incidents.filter((inc) => matchIncidentIdQuery(inc, q)).slice(0, 20);
  }, [incidents, value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (inc) => {
    onChange(inc.id);
    setOpen(false);
    onSelect?.(inc);
  };

  return (
    <div className="space-y-1" ref={containerRef}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-cyan-400" />
        <Input
          data-testid="incident-drill-search"
          aria-label="Search incidents and drills"
          className="h-10 rounded-xl border-slate-700/80 bg-slate-950/70 pl-9 pr-12 text-xs text-slate-100 shadow-inner placeholder:text-slate-500 focus-visible:border-cyan-500 focus-visible:ring-cyan-500/20"
          placeholder="Search incidents, drills, type, event, trigger…"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
          }}
          onFocus={() => value.trim() && setOpen(true)}
          autoComplete="off"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-500 hover:text-cyan-300"
          >
            Clear
          </button>
        )}
      </div>
      {open && value.trim() && results.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-xl border border-slate-700/80 bg-slate-950/95 p-1 shadow-2xl">
          {results.map((inc) => (
            <li
              key={inc.id}
              className="cursor-pointer rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-cyan-500/10"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(inc);
              }}
            >
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-semibold text-cyan-300">{inc.id}</span>
                <span className={`text-[9px] font-bold uppercase px-1 py-0.5 rounded ${inc.is_exercise ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'}`}>
                  {inc.is_exercise ? 'Drill' : 'Real'}
                </span>
              </div>
              {(inc.title || inc.incident_type) && (
                <div className="mt-0.5 truncate text-xs text-slate-400">
                  {[inc.title || inc.incident_type, inc.event, inc.trigger].filter(Boolean).join(' · ')}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && value.trim() && results.length === 0 && (
        <p className="px-1 text-xs text-slate-400">No incidents or drills match this search.</p>
      )}
    </div>
  );
};

const UserSearchAutocomplete = ({ users, onSelect, searchMessage }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    return users.filter((u) => matchUserQuery(u, query)).slice(0, 25);
  }, [users, query]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [results]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectUser = (user) => {
    setQuery(user.name || user.email || '');
    setOpen(false);
    onSelect(user);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open && query.trim()) setOpen(true);
      setHighlightIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0) {
        selectUser(results[highlightIndex] || results[0]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Search Users</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-cyan-400" />
        <Input
          ref={inputRef}
          className="h-10 rounded-xl border-slate-700/80 bg-slate-950/70 pl-9 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-cyan-500 focus-visible:ring-cyan-500/20"
          placeholder="Search users..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => query.trim() && setOpen(true)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls="user-search-listbox"
        />
        {open && query.trim() && (
          <ul
            id="user-search-listbox"
            role="listbox"
            className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-xl border border-slate-700/80 bg-slate-950/95 p-1 shadow-2xl"
          >
            {results.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-400">No users found</li>
            ) : (
              results.map((user, idx) => (
                <li
                  key={user.userId}
                  role="option"
                  aria-selected={idx === highlightIndex}
                  className={`cursor-pointer rounded-lg px-3 py-2 text-sm ${
                    idx === highlightIndex ? 'bg-cyan-500/15 text-cyan-100' : 'text-slate-200 hover:bg-slate-800/80'
                  }`}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectUser(user);
                  }}
                >
                  <div className="font-medium truncate">{user.name || user.email || 'User'}</div>
                  {(user.departmentName || user.email) && (
                    <div className="truncate text-xs text-slate-400">
                      {[user.departmentName, user.email].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  {user.location && !isUserLive(user.location) && (
                    <div className="mt-0.5 text-[10px] text-slate-500">Offline · last known location</div>
                  )}
                  {!user.location && (
                    <div className="mt-0.5 text-[10px] text-amber-400">No location on record</div>
                  )}
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      {searchMessage && (
        <p className="text-xs text-amber-400">{searchMessage}</p>
      )}
    </div>
  );
};

// Toggle-chip row (severity etc.): every chip on = no constraint; tap to narrow.
const ChipToggleFilter = ({ label, options, selected, onChange, colors = {} }) => {
  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</Label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.includes(opt.value);
          const onCls = colors[opt.value] || 'border-cyan-500/60 bg-cyan-500/15 text-cyan-200';
          return (
            <button
              key={opt.value}
              type="button"
              data-testid={`chip-${label}-${opt.value}`}
              onClick={() => toggle(opt.value)}
              className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors ${on ? onCls : 'border-slate-700 bg-slate-900/75 text-slate-500 hover:border-slate-600 hover:text-slate-300'}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Searchable multi-select combobox: closed = one compact row ("All" / "3 selected");
// open = search box + checkbox list + Select all / Clear. Empty selection = no
// constraint (shows everything), so nothing can be silently hidden by default.
const FilterCombobox = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const q = query.trim().toLowerCase();
  const visible = options.filter((o) => !q || String(o.label).toLowerCase().includes(q));
  const summary = selected.length === 0 || selected.length === options.length
    ? 'All'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label || '1 selected')
      : `${selected.length} of ${options.length}`;

  const toggle = (value) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  return (
    <div className="space-y-1" ref={containerRef}>
      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</Label>
      <button
        type="button"
        data-testid={`filter-combobox-${label}`}
        onClick={() => { setOpen((o) => !o); setQuery(''); }}
        className={`flex w-full items-center justify-between rounded-lg border px-2.5 py-2 text-sm transition-colors ${selected.length > 0 && selected.length < options.length ? 'border-cyan-500/60 bg-cyan-500/15 font-semibold text-cyan-200' : 'border-slate-700/80 bg-slate-950/65 text-slate-300 hover:border-slate-600 hover:bg-slate-900'}`}
      >
        <span className="truncate">{summary}</span>
        <svg className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
      </button>
      {open && (
        <div className="relative">
          <div className="absolute z-30 w-full rounded-lg border border-slate-700/80 bg-slate-950/95 shadow-2xl">
            <div className="border-b border-slate-800 p-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-500" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search ${label.toLowerCase()}…`}
                  className="w-full rounded border border-slate-700 bg-slate-900 py-1.5 pl-6 pr-2 text-xs text-slate-100 outline-none placeholder:text-slate-500 focus:border-cyan-500"
                  data-testid={`filter-combobox-search-${label}`}
                />
              </div>
              <div className="flex gap-3 px-0.5 pt-1">
                <button type="button" onClick={() => onChange(options.map((o) => o.value))} className="text-[10px] text-cyan-400 hover:text-cyan-300">Select all</button>
                <button type="button" onClick={() => onChange([])} className="text-[10px] text-slate-500 hover:text-slate-300">Clear (show all)</button>
              </div>
            </div>
            <div className="max-h-44 overflow-y-auto p-1">
              {visible.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`filter-option-${label}-${opt.value}`}
                  onClick={() => toggle(opt.value)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-slate-300 hover:bg-cyan-500/10"
                >
                  <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${selected.includes(opt.value) ? 'border-cyan-500 bg-cyan-500' : 'border-slate-600 bg-slate-900'}`}>
                    {selected.includes(opt.value) && (
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 111.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                    )}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              ))}
              {visible.length === 0 && <p className="px-2 py-2 text-xs text-slate-500">No matches.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FloatingMapPanel = ({
  children,
  collapsed,
  contentClassName = '',
  featured = false,
  icon: Icon,
  id,
  onToggle,
  subtitle,
  title,
  titleEyebrow,
  trailing,
}) => (
  <section
    className="pointer-events-auto flex w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950/90 shadow-[0_22px_54px_-26px_rgba(0,0,0,0.92)] backdrop-blur-xl"
    data-testid={`map-floating-panel-${id}`}
    data-featured={featured ? 'true' : 'false'}
    data-surface="tactical-dark"
  >
    <div
      className={`relative flex items-center gap-2.5 overflow-hidden ${featured
        ? 'min-h-[76px] bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/90 px-3.5 py-3'
        : 'min-h-[52px] bg-slate-950/55 px-3'} ${collapsed ? '' : 'border-b border-slate-800'}`}
      data-testid={`map-floating-panel-${id}-header`}
    >
      {featured && (
        <>
          <span aria-hidden="true" className="pointer-events-none absolute -right-8 -top-10 h-28 w-28 rounded-full bg-sky-500/20 blur-2xl" />
          <span aria-hidden="true" className="pointer-events-none absolute -bottom-12 left-10 h-24 w-24 rounded-full bg-cyan-400/10 blur-2xl" />
        </>
      )}
      <button
        type="button"
        className={`relative z-10 flex min-w-0 flex-1 items-center rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${featured ? 'gap-3' : 'gap-2.5'}`}
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-controls={`map-floating-panel-${id}-content`}
        data-testid={`map-floating-panel-${id}-toggle`}
      >
        <span className={`flex shrink-0 items-center justify-center ${featured
          ? 'h-10 w-10 rounded-[14px] bg-slate-800/90 text-cyan-200 shadow-[0_10px_24px_-14px_rgba(34,211,238,0.65)] ring-1 ring-slate-600/60'
          : 'h-8 w-8 rounded-xl bg-slate-800 text-cyan-300 shadow-[0_9px_20px_-12px_rgba(34,211,238,0.55)] ring-1 ring-slate-700'}`}>
          <Icon className={featured ? 'h-[19px] w-[19px]' : 'h-4 w-4'} aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          {titleEyebrow && (
            <span className={`truncate font-black uppercase tracking-[0.16em] ${featured
              ? 'inline-flex items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2 py-0.5 text-[8px] text-emerald-200'
              : 'block text-[8px] text-cyan-400'}`}>
              {featured && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.12)]" />}
              {titleEyebrow}
            </span>
          )}
          <span className={`block truncate font-black leading-tight ${featured ? 'mt-1 text-[15px] tracking-[-0.01em] text-slate-50' : 'text-[13px] text-slate-100'}`}>{title}</span>
          {subtitle && !collapsed && (
            <span className={`block truncate ${featured ? 'mt-1 text-[10.5px] font-medium text-cyan-100/65' : 'mt-0.5 text-[10px] text-slate-400'}`}>{subtitle}</span>
          )}
        </span>
      </button>
      {trailing}
      <button
        type="button"
        className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 text-slate-400 transition hover:border-cyan-700 hover:bg-slate-800 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        onClick={onToggle}
        aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${title}`}
        title={`${collapsed ? 'Expand' : 'Collapse'} ${title}`}
      >
        <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} aria-hidden="true" />
      </button>
    </div>
    {!collapsed && (
      <div
        id={`map-floating-panel-${id}-content`}
        className={`min-h-0 overflow-y-auto overscroll-contain bg-slate-950/45 p-3.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${contentClassName}`}
      >
        {children}
      </div>
    )}
  </section>
);

const OperationsSummaryCard = ({
  collapsed,
  expanded,
  onToggle,
  onToggleExpanded,
  operationsAreaInfo = null,
  onFocusOperationsArea,
  persistenceControl = null,
  summary,
}) => {
  const items = [
    { label: 'Incidents', value: summary.real, valueClass: 'text-red-400', dotClass: 'bg-red-400' },
    { label: 'Drills', value: summary.drills, valueClass: 'text-sky-300', dotClass: 'bg-sky-400' },
    { label: 'Active', value: summary.active, valueClass: 'text-emerald-400', dotClass: 'bg-emerald-400' },
    { label: 'Related', value: summary.relatedIncidents, valueClass: 'text-cyan-300', dotClass: 'bg-cyan-400' },
    { label: 'Teams', value: summary.teams, valueClass: 'text-slate-100', dotClass: 'bg-slate-400' },
    { label: 'Users', value: summary.users, valueClass: 'text-slate-100', dotClass: 'bg-slate-500' },
  ];
  return (
    <section
      className={`pointer-events-auto w-[286px] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950/90 shadow-[0_22px_54px_-26px_rgba(0,0,0,0.92)] backdrop-blur-xl ${collapsed ? 'w-[250px]' : ''}`}
      data-testid="map-operations-summary"
      data-surface="tactical-dark"
    >
      <div className={`flex min-h-[52px] items-center gap-2 bg-slate-950/55 px-3 ${collapsed ? '' : 'border-b border-slate-800'}`}>
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
          aria-expanded={!collapsed}
          aria-controls="map-operations-summary-content"
          data-testid="map-operations-summary-toggle"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-800 text-cyan-300 ring-1 ring-slate-700">
            <Activity className="h-4 w-4" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-[9px] font-black uppercase tracking-[0.12em] text-slate-300">Operations Summary</span>
            <span className="mt-0.5 inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12)]" />Live
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 text-slate-400 transition hover:border-cyan-700 hover:bg-slate-800 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
          aria-label={expanded ? 'Exit expanded map' : 'Expand map'}
          title={expanded ? 'Exit expanded map' : 'Expand map'}
          aria-pressed={expanded}
          data-testid="map-expand-toggle"
        >
          {expanded ? <Minimize2 className="h-4 w-4" aria-hidden="true" /> : <Maximize2 className="h-4 w-4" aria-hidden="true" />}
        </button>
        <button
          type="button"
          onClick={onToggle}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 text-slate-400 transition hover:border-cyan-700 hover:bg-slate-800 hover:text-cyan-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
          aria-label={`${collapsed ? 'Expand' : 'Collapse'} Operations Summary`}
          title={`${collapsed ? 'Expand' : 'Collapse'} Operations Summary`}
        >
          <ChevronDown className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`} aria-hidden="true" />
        </button>
      </div>
      {!collapsed && (
        <div id="map-operations-summary-content" className="p-3">
          <div className="grid grid-cols-3 gap-1.5">
            {items.map((item) => (
              <div key={item.label} className="rounded-xl border border-slate-800 bg-slate-900/75 px-2.5 py-2">
                <p className={`text-[18px] font-black leading-tight tabular-nums ${item.valueClass}`}>{item.value}</p>
                <p className="mt-1 flex items-center gap-1 text-[9px] font-semibold leading-tight text-slate-400">
                  <span className={`h-1.5 w-1.5 flex-none rounded-full ${item.dotClass}`} />{item.label}
                </p>
              </div>
            ))}
          </div>
          {operationsAreaInfo && (
            <button
              type="button"
              onClick={onFocusOperationsArea}
              className="mt-2.5 flex w-full items-start gap-2.5 rounded-xl border border-emerald-800/70 bg-emerald-500/10 px-3 py-2.5 text-left transition hover:border-emerald-600 hover:bg-emerald-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              data-testid="operations-area-map-legend"
              aria-label={`Focus Operations Area: ${operationsAreaInfo.countryName}`}
              title={operationsAreaInfo.attribution || 'Focus the map on the configured country and watch radius'}
            >
              <span className="mt-0.5 h-3 w-3 shrink-0 rounded-sm border-2 border-emerald-700 bg-emerald-500/30" aria-hidden="true" />
              <span className="min-w-0 leading-tight">
                <span className="block truncate text-[10px] font-black uppercase tracking-[0.1em] text-emerald-400">
                  Operations Area
                </span>
                <span className="mt-1 block truncate text-xs font-bold text-slate-100">
                  {operationsAreaInfo.countryName}
                </span>
                <span className="mt-0.5 block text-[10px] leading-4 text-slate-400">
                  {operationsAreaInfo.boundaryLabel} · {operationsAreaInfo.radiusLabel}
                </span>
                {operationsAreaInfo.attribution && (
                  <span className="sr-only">{operationsAreaInfo.attribution}</span>
                )}
              </span>
            </button>
          )}
          {persistenceControl && (
            <div className="mt-2.5 rounded-xl border border-emerald-800/60 bg-emerald-500/10 p-2.5">
              {persistenceControl}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const LiveUserMarker = React.memo(({ data, isSelected, departmentNameMap }) => {
  const [showInfo, setShowInfo] = useState(false);
  const position = useMemo(() => ({
    lat: parseFloat(data.lat),
    lng: parseFloat(data.lng),
  }), [data.lat, data.lng]);

  const entityType = useMemo(() => resolveEntityType(data, 'team'), [data]);
  const online = useMemo(() => isUserLive(data), [data]);

  useEffect(() => {
    if (isSelected) setShowInfo(true);
  }, [isSelected]);

  if (Number.isNaN(position.lat) || Number.isNaN(position.lng)) return null;

  const cardActions = data.active_incident_id ? (
    <AssignedIncidentAction incidentId={data.active_incident_id} />
  ) : null;

  return (
    <Marker
      position={position}
      icon={getUserMarkerIcon(isSelected, online)}
      opacity={online ? 1 : 0.7}
      title={data.user_name || 'User'}
      zIndex={isSelected ? 1000 : 600}
      onClick={() => setShowInfo(true)}
      animation={isSelected && window.google ? window.google.maps.Animation.BOUNCE : null}
    >
      {showInfo && (
        <InfoWindow onCloseClick={() => setShowInfo(false)}>
          <MapEntityCard entityType={entityType} data={data} actions={cardActions} departmentNameMap={departmentNameMap} />
        </InfoWindow>
      )}
    </Marker>
  );
});

const MapPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const focusId = searchParams.get('focus');
  const focusUserId = searchParams.get('focusUser');
  const focusWarningId = searchParams.get('focusWarning');
  const currentUser = useMemo(() => readStoredUser(), []);
  const foresightArtifactsEnabled = areForesightArtifactsEnabled(currentUser);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapsUnavailable = useMapsUnavailable();

  const [incidents, setIncidents] = useState([]);
  const [teamLocations, setTeamLocations] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Multi-select filters use "empty = no constraint" so the default view shows
  // the entire platform; picking values narrows the map.
  const [filters, setFilters] = useState({
    scope: 'all',   // all | real | drill
    severity: SEVERITY_OPTIONS.map((o) => o.value),
    status: [],
    types: [],
    events: [],
    triggers: [],
    teams: [],
    departments: [],
    crews: [],
    userStatus: 'all',
    dutyStatus: 'all',
  });

  const [appUsers, setAppUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [dutyMap, setDutyMap] = useState({}); // userId -> { status: in_duty | not_in_duty }
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userSearchMessage, setUserSearchMessage] = useState('');

  const [layers, setLayers] = useState({
    incidents: true,
    teams: true,
    links: true,
  });

  // Operations Area identifies the institution's operating geography. Its
  // display controls stay available independently of ForeSight entitlements.
  const [opsArea, setOpsArea] = useState(null);
  const [opsAreaBoundary, setOpsAreaBoundary] = useState(null);
  const [opsAreaBoundaryAttribution, setOpsAreaBoundaryAttribution] = useState(null);
  const [opsAreaPreferenceUser, setOpsAreaPreferenceUser] = useState(() => readStoredUser());
  const [opsAreaDisplay, setOpsAreaDisplay] = useState(() => loadOperationsAreaDisplay(readStoredUser()));
  const [opsAreaControlsOpen, setOpsAreaControlsOpen] = useState(false);
  const [opsAreaLoadSettled, setOpsAreaLoadSettled] = useState(false);
  const opsAreaRequestVersion = useRef(0);
  const hasInitialCentered = useRef(false);
  const defaultCenter = useMemo(() => {
    const center = opsArea?.center;
    const lat = Number(center?.latitude ?? center?.lat);
    const lng = Number(center?.longitude ?? center?.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : fallbackCenter;
  }, [opsArea]);
  const loadOperationsArea = useCallback(async (event) => {
    const requestVersion = ++opsAreaRequestVersion.current;
    setOpsAreaLoadSettled(false);
    const contextChanged = event?.type === 'saas-session-changed';
    const areaChanged = event?.type === 'operations-area-changed';
    if (contextChanged || areaChanged) {
      // Never leave the previous institution/country painted while the new
      // tenant-scoped boundary is loading. The save event can safely provide
      // the new area immediately, but its old boundary must still be removed.
      setOpsArea(areaChanged && event?.detail ? event.detail : null);
      setOpsAreaBoundary(null);
      setOpsAreaBoundaryAttribution(null);
      hasInitialCentered.current = false;
    }
    try {
      const res = await CommanServices.getOperationsAreaBoundary();
      if (requestVersion !== opsAreaRequestVersion.current) return;
      if (res?.status && res.data) {
        setOpsArea(res.data.operations_area || res.data);
        setOpsAreaBoundary(res.data.boundary || null);
        setOpsAreaBoundaryAttribution(res.data.boundary_attribution || null);
        setOpsAreaLoadSettled(true);
        return;
      }
    } catch (e) { /* handled by clearing the potentially stale overlay below */ }
    if (requestVersion === opsAreaRequestVersion.current) {
      setOpsArea(null);
      setOpsAreaBoundary(null);
      setOpsAreaBoundaryAttribution(null);
      setOpsAreaLoadSettled(true);
    }
  }, []);

  useEffect(() => {
    loadOperationsArea();
    window.addEventListener('operations-area-changed', loadOperationsArea);
    window.addEventListener('saas-session-changed', loadOperationsArea);
    window.addEventListener('focus', loadOperationsArea);
    return () => {
      opsAreaRequestVersion.current += 1;
      window.removeEventListener('operations-area-changed', loadOperationsArea);
      window.removeEventListener('saas-session-changed', loadOperationsArea);
      window.removeEventListener('focus', loadOperationsArea);
    };
  }, [loadOperationsArea]);

  useEffect(() => {
    const loadPreferenceScope = () => {
      const nextUser = readStoredUser();
      setOpsAreaPreferenceUser(nextUser);
      setOpsAreaDisplay(loadOperationsAreaDisplay(nextUser));
      setOpsAreaControlsOpen(false);
    };
    window.addEventListener('saas-session-changed', loadPreferenceScope);
    return () => window.removeEventListener('saas-session-changed', loadPreferenceScope);
  }, []);

  const updateOpsAreaDisplay = useCallback((change) => {
    setOpsAreaDisplay((current) => {
      const next = { ...current, ...change };
      saveOperationsAreaDisplay(opsAreaPreferenceUser, next);
      return next;
    });
  }, [opsAreaPreferenceUser]);

  const resetOpsAreaDisplay = useCallback(() => {
    setOpsAreaDisplay(resetOperationsAreaDisplay(opsAreaPreferenceUser));
  }, [opsAreaPreferenceUser]);

  // The command surface floats above the map so the geographic canvas always
  // keeps the full available width. Each dense control group is independently
  // collapsible to let an operator protect the area they are actively reading.
  const [floatingPanels, setFloatingPanels] = useState({
    command: false,
    incidents: true,
    users: true,
  });
  const toggleFloatingPanel = useCallback((panel) => {
    setFloatingPanels((current) => ({ ...current, [panel]: !current[panel] }));
  }, []);
  const [operationsSummaryCollapsed, setOperationsSummaryCollapsed] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const mapSurfaceRef = useRef(null);

  useEffect(() => {
    if (!mapExpanded) return undefined;
    const exitOnEscape = (event) => {
      if (event.key === 'Escape') setMapExpanded(false);
    };
    window.addEventListener('keydown', exitOnEscape);
    return () => window.removeEventListener('keydown', exitOnEscape);
  }, [mapExpanded]);

  // Early warnings — pulsating points controlled from the Operations Area toolbar.
  const [mapWarnings, setMapWarnings] = useState([]);
  const [showWarnings, setShowWarnings] = useState(true);
  const [warningPreview, setWarningPreview] = useState(null);
  useEffect(() => {
    if (!foresightArtifactsEnabled) {
      setMapWarnings([]);
      setWarningPreview(null);
      return undefined;
    }
    const loadWarnings = async () => {
      try {
        // Use the same canonical, server-scoped detection queue as ForeSight.
        // FastAPI returns this payload directly, while some deployments wrap
        // it in { status, data }; the normalizer accepts both shapes.
        const response = await CommanServices.getForesightDecisionQueue();
        if (response?.status === false) return;
        const payload = response?.data || response;
        if (!Array.isArray(payload?.items)) return;
        setMapWarnings(normalizeForesightMapWarnings(response));
      } catch (e) { /* points appear on next refresh */ }
    };
    loadWarnings();
    const interval = setInterval(loadWarnings, 60000);
    window.addEventListener('earlyWarningsChanged', loadWarnings);
    window.addEventListener('foresightRefresh', loadWarnings);
    return () => {
      clearInterval(interval);
      window.removeEventListener('earlyWarningsChanged', loadWarnings);
      window.removeEventListener('foresightRefresh', loadWarnings);
    };
  }, [foresightArtifactsEnabled]);

  // External feed events overlay (API Connections watch) — opt-in layer
  const [showExternalEvents, setShowExternalEvents] = useState(false);
  const [externalEvents, setExternalEvents] = useState([]);
  const [selectedExternalId, setSelectedExternalId] = useState(null);
  useEffect(() => {
    if (!foresightArtifactsEnabled) {
      setShowExternalEvents(false);
      setExternalEvents([]);
      setSelectedExternalId(null);
      return undefined;
    }
    if (!showExternalEvents || externalEvents.length) return;
    (async () => {
      try {
        const res = await CommanServices.getExternalEventsWatch();
        if (res?.status) setExternalEvents((res.data?.events || []).filter((e) => e.gps));
      } catch (e) { /* overlay is best-effort */ }
    })();
    return undefined;
  }, [foresightArtifactsEnabled, showExternalEvents, externalEvents.length]);

  const [mapViewType, setMapViewType] = useState(
    () => sessionStorage.getItem(MAP_VIEW_SESSION_KEY) || 'dark'
  );

  const [map, setMap] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const warningFocusTarget = useMemo(
    () => foresightMapFocusTarget(searchParams, mapWarnings),
    [searchParams, mapWarnings],
  );
  const previewedWarningFocus = useRef('');

  useEffect(() => {
    if (!focusWarningId) return;
    setShowWarnings(true);
    if (warningFocusTarget) {
      focusForesightWarningViewport(map, warningFocusTarget);
    }
    if (
      warningFocusTarget?.warning
      && previewedWarningFocus.current !== String(warningFocusTarget.id)
    ) {
      previewedWarningFocus.current = String(warningFocusTarget.id);
      setWarningPreview(warningToEvent(warningFocusTarget.warning));
    }
  }, [focusWarningId, map, warningFocusTarget]);

  const buildScenarioFromWarning = useCallback(async (event) => {
    const warningId = event?.warning_id || event?.id;
    if (!warningId) {
      toast.error('This detection is missing its authoritative ForeSight record');
      return;
    }
    try {
      const response = await CommanServices.scenarioPrefillFromWarning({
        warning_id: warningId,
      });
      const prefill = (response?.data || response)?.prefill;
      if (response?.status === false || !prefill) {
        toast.error(response?.message || 'Could not prepare this detection for Scenario Builder');
        return;
      }
      navigate('/scenario-library', { state: { agentPrefill: prefill } });
    } catch (error) {
      toast.error(error?.message || 'Could not prepare this detection for Scenario Builder');
    }
  }, [navigate]);

  const focusConfiguredOperationsArea = useCallback(() => focusOperationsAreaViewport({
    map,
    mapsApi: window.google?.maps,
    area: opsArea,
    boundary: opsAreaBoundary,
  }), [map, opsArea, opsAreaBoundary]);

  const handleOperationsAreaControl = useCallback(() => {
    setOpsAreaControlsOpen((open) => !open);
    focusConfiguredOperationsArea();
  }, [focusConfiguredOperationsArea]);

  useEffect(() => {
    if (!map || !window.google?.maps?.event) return undefined;
    const resizeTimer = window.setTimeout(() => {
      window.google.maps.event.trigger(map, 'resize');
    }, 0);
    return () => window.clearTimeout(resizeTimer);
  }, [map, mapExpanded]);

  // Draw the tenant-scoped Operations Area returned by the backend. The layer
  // cleans up and redraws whenever the country, radius or institution changes.
  useEffect(() => {
    if (!map || !opsArea || !window.google) return;
    return installOperationsAreaOverlay({
      mapsApi: window.google.maps,
      map,
      area: opsArea,
      boundary: opsAreaBoundary,
      display: opsAreaDisplay,
    });
  }, [map, opsArea, opsAreaBoundary, opsAreaDisplay]);
  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  // Clicking a relationship (dotted) line opens BOTH endpoints' cards —
  // the root incident and the related one. { parent, child } incident ids.
  const [linkCardIds, setLinkCardIds] = useState(null);
  const [incidentIdSearch, setIncidentIdSearch] = useState('');

  // ── GIS tasks & tools (frontend-only, persisted in localStorage) ──
  const { tasks: gisTasks, setTasks: setGisTasks, zones: gisZones, setZones: setGisZones, points: gisPoints, setPoints: setGisPoints } = useMapGis();
  const [gisAddMode, setGisAddMode] = useState(false);
  const [gisDrawShape, setGisDrawShape] = useState('none');
  const [gisHeatmap, setGisHeatmap] = useState(false);
  const [gisSequence, setGisSequence] = useState(false);
  const [gisImportance, setGisImportance] = useState('response');
  const [gisCategory, setGisCategory] = useState('Rescue');
  const [gisSelectedTaskId, setGisSelectedTaskId] = useState(null);
  const [gisDraft, setGisDraft] = useState(null);
  const [gisPointMode, setGisPointMode] = useState(false);
  const [gisPointType, setGisPointType] = useState('assembly');
  const [gisPointDraft, setGisPointDraft] = useState(null);
  const [gisActionCards, setGisActionCards] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await CommanServices.getactioncardsData();
        const list = Array.isArray(res) ? res : (res?.data?.action_cards || res?.data || []);
        setGisActionCards(Array.isArray(list) ? list : []);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const handleGisMapClick = (e) => {
    const position = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    if (gisPointMode) {
      setGisPointDraft({ position, type: gisPointType, label: '', info: '', details: [], incidentId: focusId || undefined });
      setGisPointMode(false);
      return;
    }
    if (!gisAddMode) return;
    setGisDraft({ position, title: '', note: '', category: activeCategory, importance: activeImportance, status: 'open', incidentId: focusId || undefined });
    setGisAddMode(false);
  };

  const saveGisTask = (form) => {
    setActiveTasks((prev) => {
      if (form.id) return prev.map((t) => (t.id === form.id ? { ...t, ...form } : t));
      return [...prev, { ...form, id: `task_${Date.now()}`, createdAt: Date.now(), incidentId: focusId || undefined }];
    });
    setGisDraft(null);
  };

  const saveGisPoint = (form) => {
    setActivePoints((prev) => {
      if (form.id) return prev.map((p) => (p.id === form.id ? { ...p, ...form } : p));
      return [...prev, { ...form, id: `pt_${Date.now()}`, createdAt: Date.now(), incidentId: focusId || undefined }];
    });
    setGisPointDraft(null);
  };

  const teamOptions = useMemo(
    () => allTeams.map((t) => ({ value: t.id, label: t.team_name || 'Unnamed Team' })),
    [allTeams]
  );

  const crewOptions = useMemo(() => {
    const out = [];
    allTeams.forEach((t) => (t.crews || []).forEach((c) => {
      if (c?.id && c.is_active !== false) out.push({ value: c.id, label: `${c.crew_name || 'Crew'} — ${t.team_name || 'Team'}` });
    }));
    return out;
  }, [allTeams]);

  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.department_name || d.name || 'Department' })),
    [departments]
  );

  const departmentNameMap = useMemo(
    () => buildDepartmentNameMap(departments),
    [departments]
  );

  useEffect(() => {
    sessionStorage.setItem(MAP_VIEW_SESSION_KEY, mapViewType);
  }, [mapViewType]);

  useEffect(() => {
    if (!map || !window.google) return;
    map.setOptions(buildMapOptions(mapViewType));
  }, [map, mapViewType]);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const incidentRes = await CommanServices.getIncidentsData();
      const teamRes = await CommanServices.getTeamLocations();
      const locRes = await CommanServices.getlocationszonesdata();
      const allTeamsRes = await CommanServices.getTeamsData();

      if (incidentRes && incidentRes.status) {
        setIncidents(incidentRes.data || []);
      }

      if (teamRes) {
        let rawTeams = [];
        if (teamRes.status && Array.isArray(teamRes.data)) {
          rawTeams = teamRes.data;
        } else if (Array.isArray(teamRes)) {
          rawTeams = teamRes;
        }
        setTeamLocations(normalizeLocations(rawTeams));
      }

      if (locRes && locRes.status) {
        setLocations(locRes.data || []);
      }

      if (allTeamsRes && allTeamsRes.status) {
        setAllTeams(allTeamsRes.data || []);
      } else if (Array.isArray(allTeamsRes)) {
        setAllTeams(allTeamsRes);
      }

      const appUsersRes = await CommanServices.getAppUsers();
      if (appUsersRes?.status && Array.isArray(appUsersRes.data)) {
        setAppUsers(appUsersRes.data);
      } else if (Array.isArray(appUsersRes)) {
        setAppUsers(appUsersRes);
      }

      const deptRes = await CommanServices.departments();
      const rawDepts = Array.isArray(deptRes)
        ? deptRes
        : (deptRes?.status && Array.isArray(deptRes.data) ? deptRes.data : []);
      setDepartments(rawDepts.map((d) => ({ ...d, id: d.id || d._id })));

      // Latest duty-check responses — powers the On duty / Off duty / No response filter.
      try {
        const dutyRes = await axios.get(`${ApiEndPoint}/api/duty-status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        });
        setDutyMap(dutyRes.data?.data?.users || {});
      } catch (_) { /* duty filter degrades to 'no response' for everyone */ }
    } catch (err) {
      console.error('Map: Error fetching data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // (No auto-fill of team selection — empty selection means "all teams", and an
  // auto-fill effect used to fight the user's Clear action.)

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => console.warn('Geolocation access denied or failed:', error)
      );
    }
  }, []);

  const mergeLocationEntry = useCallback((data, options = {}) => {
    if (!data) return;
    const key = getLocationKey(data);
    setTeamLocations((prev) => {
      const locMap = new Map(prev.map((loc) => [getLocationKey(loc), loc]));
      const existing = locMap.get(key);
      const lat = parseFloat(data.lat ?? existing?.lat);
      const lng = parseFloat(data.lng ?? existing?.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return prev;

      locMap.set(key, {
        ...(existing || {}),
        ...data,
        lat,
        lng,
        status: options.offline ? 'offline' : (data.status || existing?.status || 'active'),
        timestamp: data.timestamp ? parseTimestamp(data.timestamp) : (options.offline ? existing?.timestamp : Date.now()),
      });
      return Array.from(locMap.values());
    });
  }, []);

  const applyLocationUpdate = useCallback((data) => {
    if (!data) return;
    if (data.status === 'offline') {
      mergeLocationEntry(data, { offline: true });
      return;
    }
    const lat = parseFloat(data.lat);
    const lng = parseFloat(data.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    mergeLocationEntry({ ...data, lat, lng, status: 'active' });
  }, [mergeLocationEntry]);

  const markUserOffline = useCallback((data) => {
    if (!data?.user_id) return;
    mergeLocationEntry(data, { offline: true });
  }, [mergeLocationEntry]);

  const applyDutyUpdate = useCallback((data) => {
    if (!data?.user_id) return;
    mergeLocationEntry(data);
    if (data.is_active !== undefined) {
      setAppUsers((prev) => prev.map((u) => (
        String(u.id) === String(data.user_id)
          ? { ...u, is_active: data.is_active }
          : u
      )));
    }
    // Live duty responses (e.g. the mobile Check-in quick action) feed the
    // On duty / Off duty filter without waiting for a refetch.
    if (data.duty_status) {
      setDutyMap((prev) => ({
        ...prev,
        [String(data.user_id)]: {
          status: data.duty_status,
          responded_at: data.responded_at || null,
        },
      }));
    }
  }, [mergeLocationEntry]);

  useEffect(() => {
    fetchData();

    const handleIncidentSync = async (event = {}) => {
      const sync = event?.detail || event;
      const incidentId = sync?.incident_id || sync?.incidentId;
      const changeType = String(sync?.change_type || '').toLowerCase();

      if (incidentId && ['gis_map_updated', 'scenario_map_applied'].includes(changeType)) {
        window.dispatchEvent(new CustomEvent('gisMapUpdated', { detail: { incidentId } }));
        return;
      }
      if (!incidentId) {
        await fetchData(true);
        return;
      }

      try {
        const response = await CommanServices.getIncidentDetail(incidentId);
        const updatedIncident = response?.data ?? response;
        if (!updatedIncident?.id) {
          if (changeType.includes('delete') || changeType.includes('deactivate')) {
            setIncidents((previous) => previous.filter((incident) => String(incident.id) !== String(incidentId)));
          }
          return;
        }
        setIncidents((previous) => {
          const index = previous.findIndex((incident) => String(incident.id) === String(updatedIncident.id));
          if (index < 0) return [updatedIncident, ...previous];
          const next = [...previous];
          next[index] = updatedIncident;
          return next;
        });
      } catch (error) {
        console.error('Map: Failed to refresh affected incident:', error);
      }
    };

    const handleTeamUpdate = (data) => {
      if (data?.is_active !== undefined && data.lat == null && data.lng == null) {
        applyDutyUpdate(data);
        return;
      }
      applyLocationUpdate(data);
    };

    const unsubscribeLocation = socketService.on('team_location_updated', handleTeamUpdate);
    const unsubscribeDuty = socketService.on('user_duty_status_updated', applyDutyUpdate);
    const unsubscribeOffline = socketService.on('team_location_offline', markUserOffline);
    const unsubscribeIncident = socketService.on('receive_incident_sync', handleIncidentSync);

    window.addEventListener('incidentRealtimeSync', handleIncidentSync);
    window.addEventListener('dashboardRefresh', handleIncidentSync);

    return () => {
      unsubscribeLocation();
      unsubscribeDuty();
      unsubscribeOffline();
      unsubscribeIncident();
      window.removeEventListener('incidentRealtimeSync', handleIncidentSync);
      window.removeEventListener('dashboardRefresh', handleIncidentSync);
    };
  }, [fetchData, applyLocationUpdate, applyDutyUpdate, markUserOffline]);

  const incidentsWithCoords = useMemo(() => {
    const zoneIndex = buildZoneIndex(locations);
    const coordsCount = new Map();

    // Platform-wide geospatial view: REAL incidents and drills/exercises together.
    return incidents
      .filter((inc) => inc?.is_planned_event !== true || inc?.is_exercise === true)
      .map((inc) => {
      const resolved = resolveIncidentCoords(zoneIndex, inc);
      if (!resolved) return null;
      const baseLat = resolved.lat;
      const baseLng = resolved.lng;

      const key = `${baseLat},${baseLng}`;
      const count = coordsCount.get(key) || 0;
      coordsCount.set(key, count + 1);
      const jitter = 0.00003;
      const angle = (count * 137.5) * (Math.PI / 180);
      const r = jitter * Math.sqrt(count);
      // Attach the resolved zone name so the marker InfoWindow can label it.
      const zoneName = resolved.name || resolved.matchedName || '';
      return {
        ...inc,
        location_name: inc.location_name || zoneName,
        zone_name: zoneName,
        lat: parseFloat(baseLat) + (count > 0 ? Math.cos(angle) * r : 0),
        lng: parseFloat(baseLng) + (count > 0 ? Math.sin(angle) * r : 0),
      };
      })
      .filter(Boolean);
  }, [incidents, locations]);

  const filteredIncidents = useMemo(() => {
    return incidentsWithCoords.filter((inc) => {
      if (filters.scope !== 'all') {
        const kind = inc.is_exercise ? 'drill' : 'real';
        if (kind !== filters.scope) return false;
      }

      if (filters.severity.length > 0 && filters.severity.length < SEVERITY_OPTIONS.length) {
        const sev = normalizeSeverity(inc.severity);
        if (!filters.severity.includes(sev)) return false;
      }

      // Exact lifecycle status (empty selection = no constraint)
      if (filters.status.length > 0 && !filters.status.includes(String(inc.status || '').toLowerCase())) return false;

      // Classification filters (empty selection = no constraint)
      if (filters.types.length > 0 && !filters.types.includes(inc.incident_type || '—')) return false;
      if (filters.events.length > 0 && !filters.events.includes(inc.event || '—')) return false;
      if (filters.triggers.length > 0 && !filters.triggers.includes(inc.trigger || '—')) return false;

      return true;
    });
  }, [incidentsWithCoords, filters]);

  // Distinct classification values present in the data → filter options.
  const classificationOptions = useMemo(() => {
    const collect = (field) => {
      const s = new Set();
      incidentsWithCoords.forEach((i) => s.add(i[field] || '—'));
      return [...s].sort().map((v) => ({ value: v, label: v }));
    };
    return { types: collect('incident_type'), events: collect('event'), triggers: collect('trigger') };
  }, [incidentsWithCoords]);

  // Status options follow the selected operational scope. Legacy database
  // values are never exposed as choices.
  const statusOptions = useMemo(() => {
    const inData = new Map();
    incidentsWithCoords.forEach((i) => {
      const s = String(i.status || '').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(STATUS_LABELS, s)) {
        inData.set(s, (inData.get(s) || 0) + 1);
      }
    });
    const all = filters.scope === 'real'
      ? ['open', 'in_progress', 'resolved']
      : filters.scope === 'drill'
        ? ['requested', 'active', 'completed', 'resolved']
        : ['open', 'in_progress', 'requested', 'active', 'completed', 'resolved'];
    all.sort((a, b) => (inData.get(b) || 0) - (inData.get(a) || 0) || statusLabel(a).localeCompare(statusLabel(b)));
    return all.map((s) => ({
      value: s,
      label: inData.has(s) ? `${statusLabel(s)} (${inData.get(s)})` : statusLabel(s),
    }));
  }, [incidentsWithCoords, filters.scope]);

  const mapVisibleIncidents = useMemo(() => {
    const q = incidentIdSearch.trim().toLowerCase();
    if (!q) return filteredIncidents;
    return filteredIncidents.filter((inc) => matchIncidentIdQuery(inc, q));
  }, [filteredIncidents, incidentIdSearch]);

  // ── Dedicated scenario view ────────────────────────────────────────────────
  // When opened via a scenario's "Full Map" (?focus=<id>&scenario=1), scenarios
  // are excluded from /api/incidents, so we fetch the focused incident directly
  // and show ONLY it + its own GIS tasks/zones.
  const scenarioParam = searchParams.get('scenario') === '1';
  const [focusedIncident, setFocusedIncident] = useState(null);
  useEffect(() => {
    if (!focusId) { setFocusedIncident(null); return; }
    (async () => {
      try {
        const res = await CommanServices.getIncidentDetail(focusId);
        const inc = res?.data ?? res;
        setFocusedIncident(inc && inc.id && inc.is_planned_event !== true ? inc : null);
      } catch { setFocusedIncident(null); }
    })();
  }, [focusId]);

  const scenarioFocus = !!focusId && (scenarioParam || !!focusedIncident?.is_scenario);
  // Persist the GIS map to the backend whenever a scenario OR a drill (exercise) is focused.
  const dbMapFocus = !!focusId && (scenarioParam || !!focusedIncident?.is_scenario || !!focusedIncident?.is_exercise);
  const persistentGis = usePersistentGisMap(focusId, dbMapFocus);

  // Active GIS state — backend-backed when a scenario/drill is focused, else localStorage.
  const activeTasks = dbMapFocus ? persistentGis.tasks : gisTasks;
  const activeZones = dbMapFocus ? persistentGis.zones : gisZones;
  const activePoints = dbMapFocus ? persistentGis.points : gisPoints;
  const setActiveTasks = dbMapFocus ? persistentGis.setTasks : setGisTasks;
  const setActiveZones = dbMapFocus ? persistentGis.setZones : setGisZones;
  const setActivePoints = dbMapFocus ? persistentGis.setPoints : setGisPoints;
  const activeHeatmap = dbMapFocus ? !!persistentGis.settings.heatmapOn : gisHeatmap;
  const activeSequence = dbMapFocus ? !!persistentGis.settings.sequenceOn : gisSequence;
  const activeImportance = dbMapFocus ? (persistentGis.settings.importance || 'response') : gisImportance;
  const activeCategory = dbMapFocus ? (persistentGis.settings.category || 'Rescue') : gisCategory;
  const setActiveHeatmap = dbMapFocus
    ? (u) => persistentGis.setSettings((s) => ({ ...s, heatmapOn: typeof u === 'function' ? u(!!s.heatmapOn) : u }))
    : setGisHeatmap;
  const setActiveSequence = dbMapFocus
    ? (u) => persistentGis.setSettings((s) => ({ ...s, sequenceOn: typeof u === 'function' ? u(!!s.sequenceOn) : u }))
    : setGisSequence;
  const setActiveImportance = dbMapFocus
    ? (v) => persistentGis.setSettings((s) => ({ ...s, importance: v }))
    : setGisImportance;
  const setActiveCategory = dbMapFocus
    ? (v) => persistentGis.setSettings((s) => ({ ...s, category: v }))
    : setGisCategory;

  const focusedIncidentWithCoords = useMemo(() => {
    if (!focusedIncident) return null;
    const zoneIndex = buildZoneIndex(locations);
    const resolved = resolveIncidentCoords(zoneIndex, focusedIncident);
    if (!resolved) return null;
    const zoneName = resolved.name || resolved.matchedName || '';
    // Scenarios carry a stale `title`; show the scenario type consistently.
    const scenarioLabel = focusedIncident.incident_type || focusedIncident.title || 'Scenario';
    return { ...focusedIncident, title: scenarioLabel, location_name: focusedIncident.location_name || zoneName, zone_name: zoneName, lat: resolved.lat, lng: resolved.lng };
  }, [focusedIncident, locations]);

  // Markers + GIS to render: scenario view = only this scenario & its drawings.
  const markersToShow = useMemo(() => {
    if (scenarioFocus) return focusedIncidentWithCoords ? [focusedIncidentWithCoords] : [];
    return mapVisibleIncidents;
  }, [scenarioFocus, focusedIncidentWithCoords, mapVisibleIncidents]);

  const gisTasksToShow = dbMapFocus ? activeTasks : gisTasks;
  const gisZonesToShow = dbMapFocus ? activeZones : gisZones;
  const gisPointsToShow = dbMapFocus ? activePoints : gisPoints;

  const usersById = useMemo(() => new Map(appUsers.map((u) => [String(u.id), u])), [appUsers]);

  // Selected departments match by BOTH id and name — user records reference
  // departments either way (departments_units / department_unit / department).
  const selectedDeptKeys = useMemo(() => {
    const s = new Set();
    filters.departments.forEach((id) => {
      s.add(String(id));
      const d = departments.find((x) => String(x.id) === String(id));
      if (d?.department_name) s.add(String(d.department_name));
    });
    return s;
  }, [filters.departments, departments]);

  const filteredTeamLocations = useMemo(() => {
    return teamLocations.filter((team) => {
      if (filters.teams.length > 0 && team.team_id && !filters.teams.includes(team.team_id)) {
        return false;
      }
      const live = isUserLive(team);
      if (filters.userStatus === 'online' && !live) return false;
      if (filters.userStatus === 'offline' && live) return false;

      const user = team.user_id ? usersById.get(String(team.user_id)) : null;

      // Duty status (latest duty-check response)
      if (filters.dutyStatus !== 'all') {
        const st = dutyMap[String(team.user_id)]?.status;
        const duty = st === 'in_duty' ? 'in_duty' : st === 'not_in_duty' ? 'not_in_duty' : 'no_response';
        if (duty !== filters.dutyStatus) return false;
      }

      // Departments (empty selection = no constraint)
      if (filters.departments.length > 0) {
        const refs = [
          ...(user?.departments_units || []),
          user?.department_unit, user?.department,
          team.department_unit, team.department,
        ].filter(Boolean).map(String);
        if (!refs.some((r) => selectedDeptKeys.has(r))) return false;
      }

      // Crews (empty selection = no constraint)
      if (filters.crews.length > 0) {
        const crewId = String(team.crew_id || user?.crew_id || '');
        if (!crewId || !filters.crews.includes(crewId)) return false;
      }

      return true;
    });
  }, [teamLocations, filters.teams, filters.userStatus, filters.dutyStatus, filters.departments, filters.crews, usersById, dutyMap, selectedDeptKeys]);

  const searchableUsers = useMemo(() => {
    const locByUserId = new Map();
    teamLocations.forEach((loc) => {
      if (loc.user_id) locByUserId.set(String(loc.user_id), loc);
    });

    if (appUsers.length > 0) {
      return appUsers.map((u) => {
        const location = locByUserId.get(String(u.id)) || null;
        const merged = { ...u, ...location };
        return {
          userId: String(u.id),
          name: u.name || '',
          email: u.email || '',
          username: (u.email || '').split('@')[0] || '',
          departmentName: getDepartmentDisplay(merged, departmentNameMap),
          location,
        };
      });
    }

    return teamLocations
      .filter((loc) => loc.user_id)
      .map((loc) => ({
        userId: String(loc.user_id),
        name: loc.user_name || '',
        email: '',
        username: '',
        departmentName: getDepartmentDisplay(loc, departmentNameMap),
        location: loc,
      }));
  }, [appUsers, teamLocations, departmentNameMap]);

  const focusUserOnMap = useCallback((user) => {
    const loc = user.location || teamLocations.find((t) => String(t.user_id) === String(user.userId));
    const lat = parseFloat(loc?.lat);
    const lng = parseFloat(loc?.lng);

    if (!loc || Number.isNaN(lat) || Number.isNaN(lng)) {
      setUserSearchMessage(`No location on record for ${user.name || user.email || 'this user'}.`);
      return;
    }

    setUserSearchMessage('');
    setSelectedUserId(String(user.userId));
    setLayers((prev) => ({ ...prev, teams: true }));
    if (map) {
      map.panTo({ lat, lng });
      map.setZoom(17);
    }
  }, [map, teamLocations]);

  const mapSummary = useMemo(() => {
    const visibleIds = new Set(mapVisibleIncidents.map((i) => i.id));
    let real = 0;
    let drills = 0;
    let active = 0;
    let relatedIncidents = 0;

    mapVisibleIncidents.forEach((inc) => {
      if (inc.is_exercise) drills += 1; else real += 1;
      const s = String(inc.status || '').toLowerCase();
      // "Active" covers everything still in play: real incidents sit at
      // open/acknowledged/…, drills at active — resolved/closed/cancelled are out.
      if (!['resolved', 'closed', 'cancelled', 'completed'].includes(s)) active += 1;

      const parentId = getIncidentRelationId(inc);
      const isChild = parentId && visibleIds.has(parentId);
      const isParent = mapVisibleIncidents.some((c) => getIncidentRelationId(c) === inc.id);
      if (isChild || isParent) relatedIncidents += 1;
    });

    const teamIds = new Set();
    let users = 0;
    filteredTeamLocations.forEach((loc) => {
      if (loc.user_id) users += 1;
      if (loc.team_id) teamIds.add(String(loc.team_id));
    });

    return {
      real,
      drills,
      active,
      relatedIncidents,
      teams: teamIds.size,
      users,
    };
  }, [mapVisibleIncidents, filteredTeamLocations]);

  const filteredIncidentIds = useMemo(
    () => new Set(mapVisibleIncidents.map((i) => i.id)),
    [mapVisibleIncidents]
  );

  const incidentById = useMemo(
    () => new Map(mapVisibleIncidents.map((i) => [i.id, i])),
    [mapVisibleIncidents]
  );

  const incidentRelations = useMemo(() => {
    const parentByChild = new Map();
    const childrenByParent = new Map();
    mapVisibleIncidents.forEach((inc) => {
      const parentId = getIncidentRelationId(inc);
      if (!parentId || !filteredIncidentIds.has(parentId)) return;
      parentByChild.set(inc.id, parentId);
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(inc);
    });
    return { parentByChild, childrenByParent };
  }, [mapVisibleIncidents, filteredIncidentIds]);

  const highlightedRelatedIds = useMemo(() => {
    if (!selectedIncidentId) return new Set();
    const ids = new Set();
    const parentId = incidentRelations.parentByChild.get(selectedIncidentId);
    if (parentId) ids.add(parentId);
    (incidentRelations.childrenByParent.get(selectedIncidentId) || []).forEach((child) => {
      ids.add(child.id);
    });
    return ids;
  }, [selectedIncidentId, incidentRelations]);

  const focusIncidentOnMap = useCallback((incidentId) => {
    const target = incidentById.get(incidentId)
      || filteredIncidents.find((i) => i.id === incidentId);
    if (!target) return;
    setSelectedIncidentId(incidentId);
    if (map) {
      map.panTo({ lat: parseFloat(target.lat), lng: parseFloat(target.lng) });
      if (map.getZoom() < 15) map.setZoom(15);
    }
  }, [incidentById, filteredIncidents, map]);

  useEffect(() => {
    const q = incidentIdSearch.trim().toLowerCase();
    if (!q) return;
    const matches = filteredIncidents.filter((inc) => matchIncidentIdQuery(inc, q));
    if (matches.length === 1) {
      focusIncidentOnMap(matches[0].id);
    }
  }, [incidentIdSearch, filteredIncidents, focusIncidentOnMap]);

  useEffect(() => {
    if (!map || !window.google?.maps || hasInitialCentered.current || !opsAreaLoadSettled) return;
    if (focusId || focusUserId || focusWarningId) {
      hasInitialCentered.current = true;
      return;
    }
    if (focusConfiguredOperationsArea()) {
      hasInitialCentered.current = true;
      return;
    }
    if (mapVisibleIncidents.length > 0 || userLocation) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;
      if (userLocation) {
        bounds.extend(userLocation);
        hasPoints = true;
      }
      mapVisibleIncidents.forEach((inc) => {
        bounds.extend({ lat: parseFloat(inc.lat), lng: parseFloat(inc.lng) });
        hasPoints = true;
      });
      if (hasPoints) {
        map.fitBounds(bounds);
        const listener = window.google.maps.event.addListener(map, 'idle', () => {
          if (map.getZoom() > 16) map.setZoom(16);
          window.google.maps.event.removeListener(listener);
        });
        hasInitialCentered.current = true;
      }
    }
  }, [
    map,
    mapVisibleIncidents,
    userLocation,
    focusId,
    focusUserId,
    focusWarningId,
    opsAreaLoadSettled,
    focusConfiguredOperationsArea,
  ]);

  useEffect(() => {
    if (focusUserId) setSelectedUserId(String(focusUserId));
  }, [focusUserId]);

  useEffect(() => {
    if (focusId) setSelectedIncidentId(focusId);
  }, [focusId]);

  useEffect(() => {
    if (map && (focusId || focusUserId)) {
      if (focusId) {
        const target = incidentsWithCoords.find((i) => i.id === focusId) || focusedIncidentWithCoords;
        if (target && target.lat != null && target.lng != null) {
          map.panTo({ lat: parseFloat(target.lat), lng: parseFloat(target.lng) });
          map.setZoom(17);
        }
      } else if (focusUserId && teamLocations.length > 0) {
        const target = teamLocations.find((t) => String(t.user_id) === String(focusUserId));
        if (target) {
          map.panTo({ lat: parseFloat(target.lat), lng: parseFloat(target.lng) });
          map.setZoom(17);
        }
      }
    }
  }, [map, focusId, focusUserId, incidentsWithCoords, focusedIncidentWithCoords, teamLocations]);

  const relationshipLines = useMemo(() => {
    if (!layers.links) return [];

    return mapVisibleIncidents.flatMap((inc) => {
      const parentId = getIncidentRelationId(inc);
      if (!parentId || !filteredIncidentIds.has(parentId)) return [];

      const parent = incidentById.get(parentId);
      if (!parent) return [];

      const parentLat = parseFloat(parent.lat);
      const parentLng = parseFloat(parent.lng);
      const childLat = parseFloat(inc.lat);
      const childLng = parseFloat(inc.lng);
      if ([parentLat, parentLng, childLat, childLng].some(Number.isNaN)) return [];

      const highlighted =
        selectedIncidentId === parent.id ||
        selectedIncidentId === inc.id ||
        (linkCardIds?.parent === parent.id && linkCardIds?.child === inc.id);

      return [{
        id: `${parent.id}-${inc.id}`,
        parentId: parent.id,
        childId: inc.id,
        highlighted,
        path: [
          { lat: parentLat, lng: parentLng },
          { lat: childLat, lng: childLng },
        ],
      }];
    });
  }, [mapVisibleIncidents, filteredIncidentIds, incidentById, layers.links, selectedIncidentId, linkCardIds]);

  const mapOptions = useMemo(() => buildMapOptions(mapViewType), [mapViewType]);

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-sky-600" />
        <p className="text-slate-400 font-medium">Initializing Map Module...</p>
      </div>
    );
  }

  return (
    <div className="relative h-full flex-1 overflow-hidden bg-slate-100 text-slate-100">
      <main
        ref={mapSurfaceRef}
        className={`${mapExpanded
          ? 'fixed inset-0 z-[100] h-screen w-screen bg-slate-950'
          : 'relative h-full w-full min-w-0'}`}
        data-testid="live-command-map"
        data-expanded={mapExpanded ? 'true' : 'false'}
        style={{ fontFamily: "'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
      >
        <div
          className="pointer-events-none absolute left-3 top-3 z-30 flex max-h-[46%] w-[calc(100%-1.5rem)] flex-col gap-2 overflow-y-auto overscroll-contain pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:bottom-3 lg:max-h-none lg:w-[320px]"
          data-testid="map-floating-control-dock"
        >
          <FloatingMapPanel
            id="command"
            featured
            icon={Globe}
            title="Geo Ops"
            subtitle="Live view — incidents, drills & responders"
            collapsed={floatingPanels.command}
            onToggle={() => toggleFloatingPanel('command')}
            trailing={(
              <button
                type="button"
                onClick={() => fetchData(true)}
                disabled={refreshing}
                className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900/80 text-cyan-200 transition hover:border-cyan-700 hover:bg-slate-800 hover:text-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:cursor-wait disabled:opacity-60"
                aria-label="Refresh live command map"
                title="Refresh live command map"
                data-testid="map-refresh"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
              </button>
            )}
          >
            <div>
              <p className="mb-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Map view</p>
              <div className="grid grid-cols-3 gap-1.5">
                {MAP_VIEW_OPTIONS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setMapViewType(id)}
                    aria-pressed={mapViewType === id}
                    data-testid={`map-view-${id}`}
                    className={`flex min-h-[38px] items-center justify-center gap-1.5 rounded-xl px-1.5 py-2 text-[10px] font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 ${mapViewType === id
                      ? 'bg-cyan-500/15 text-cyan-100 shadow-[0_8px_18px_-12px_rgba(34,211,238,0.65)] ring-1 ring-cyan-500/50'
                      : 'bg-slate-900/80 text-slate-400 hover:bg-slate-800 hover:text-cyan-200'}`}
                  >
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${mapViewType === id ? 'text-cyan-300' : 'text-slate-500'}`} aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </FloatingMapPanel>

          <FloatingMapPanel
            id="incidents"
            icon={Filter}
            title="Incident & Drill Filters"
            collapsed={floatingPanels.incidents}
            onToggle={() => toggleFloatingPanel('incidents')}
            contentClassName="space-y-4"
          >
            <IncidentIdSearch
              incidents={filteredIncidents}
              value={incidentIdSearch}
              onChange={setIncidentIdSearch}
              onSelect={(inc) => focusIncidentOnMap(inc.id)}
            />
            <div className="grid grid-cols-3 gap-1 rounded-xl border border-slate-800 bg-slate-950/80 p-1">
              {SCOPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  data-testid={`scope-${opt.value}`}
                  onClick={() => setFilters((prev) => ({ ...prev, scope: opt.value }))}
                  aria-pressed={filters.scope === opt.value}
                  className={`rounded-lg px-1.5 py-2 text-[10.5px] font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${filters.scope === opt.value
                    ? 'bg-cyan-500/15 text-cyan-100 shadow-sm ring-1 ring-cyan-500/50'
                    : 'text-slate-500 hover:bg-slate-800 hover:text-slate-200'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <ChipToggleFilter
              label="Severity"
              options={SEVERITY_OPTIONS}
              selected={filters.severity}
              onChange={(val) => setFilters((prev) => ({ ...prev, severity: val }))}
              colors={{
                high: 'border-red-500/60 bg-red-500/15 text-red-200',
                medium: 'border-amber-500/60 bg-amber-500/15 text-amber-200',
                low: 'border-yellow-500/60 bg-yellow-500/15 text-yellow-200',
              }}
            />
            <FilterCombobox label="Status" options={statusOptions} selected={filters.status} onChange={(val) => setFilters((prev) => ({ ...prev, status: val }))} />
            <FilterCombobox label="Incident Type" options={classificationOptions.types} selected={filters.types} onChange={(val) => setFilters((prev) => ({ ...prev, types: val }))} />
            <FilterCombobox label="Event" options={classificationOptions.events} selected={filters.events} onChange={(val) => setFilters((prev) => ({ ...prev, events: val }))} />
            <FilterCombobox label="Trigger" options={classificationOptions.triggers} selected={filters.triggers} onChange={(val) => setFilters((prev) => ({ ...prev, triggers: val }))} />
          </FloatingMapPanel>

          <FloatingMapPanel
            id="users"
            icon={Users}
            title="User Filters"
            collapsed={floatingPanels.users}
            onToggle={() => toggleFloatingPanel('users')}
            contentClassName="space-y-4"
          >
            <UserSearchAutocomplete
              users={searchableUsers}
              onSelect={focusUserOnMap}
              searchMessage={userSearchMessage}
            />
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Connection Status</Label>
              <div className="grid grid-cols-3 gap-1">
                {USER_STATUS_FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFilters((prev) => ({ ...prev, userStatus: opt.value }))}
                    aria-pressed={filters.userStatus === opt.value}
                    className={`rounded-lg border px-2 py-2 text-[10.5px] font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${filters.userStatus === opt.value
                      ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-100 shadow-[0_8px_18px_-12px_rgba(34,211,238,0.55)]'
                      : 'border-slate-700 bg-slate-900/75 text-slate-400 hover:border-slate-600 hover:bg-slate-800 hover:text-slate-200'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Duty Status</Label>
              <div className="grid grid-cols-2 gap-1">
                {DUTY_FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    data-testid={`duty-filter-${opt.value}`}
                    onClick={() => setFilters((prev) => ({ ...prev, dutyStatus: opt.value }))}
                    aria-pressed={filters.dutyStatus === opt.value}
                    className={`rounded-lg border px-2 py-2 text-[10.5px] font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 ${filters.dutyStatus === opt.value
                      ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-100 shadow-[0_8px_18px_-12px_rgba(34,211,238,0.55)]'
                      : 'border-slate-700 bg-slate-900/75 text-slate-400 hover:border-slate-600 hover:bg-slate-800 hover:text-slate-200'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <FilterCombobox label="Teams" options={teamOptions} selected={filters.teams} onChange={(val) => setFilters((prev) => ({ ...prev, teams: val }))} />
            <FilterCombobox label="Crews" options={crewOptions} selected={filters.crews} onChange={(val) => setFilters((prev) => ({ ...prev, crews: val }))} />
            <FilterCombobox label="Departments" options={departmentOptions} selected={filters.departments} onChange={(val) => setFilters((prev) => ({ ...prev, departments: val }))} />
          </FloatingMapPanel>

          <MapGisPanel
            variant="floating"
            floatingPlacement="flow"
            floatingDefaultCollapsed
            tasks={gisTasksToShow}
            addMode={gisAddMode} setAddMode={setGisAddMode}
            drawShape={gisDrawShape} setDrawShape={setGisDrawShape}
            heatmapOn={activeHeatmap} setHeatmapOn={setActiveHeatmap}
            sequenceOn={activeSequence} setSequenceOn={setActiveSequence}
            activeImportance={activeImportance} setActiveImportance={setActiveImportance}
            newCategory={activeCategory} setNewCategory={setActiveCategory}
            pointMode={gisPointMode} setPointMode={setGisPointMode}
            pointType={gisPointType} setPointType={setGisPointType}
            points={gisPointsToShow}
            onClear={() => { setActiveTasks([]); setActiveZones([]); setActivePoints([]); }}
          />

          <div
            className="pointer-events-auto w-full space-y-2"
            data-testid="map-operations-area-dock-controls"
          >
            <button
              type="button"
              className="flex h-10 w-full items-center justify-between gap-2 rounded-xl border border-emerald-800/70 bg-slate-950/90 px-3 text-xs font-semibold text-emerald-300 shadow-[0_16px_38px_-24px_rgba(0,0,0,0.9)] backdrop-blur transition hover:border-emerald-700 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-950"
              onClick={handleOperationsAreaControl}
              aria-expanded={opsAreaControlsOpen}
              aria-controls="operations-area-display-controls"
              aria-label="Focus and adjust Operations Area"
              data-testid="operations-area-display-button"
            >
              <span className="flex min-w-0 items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">Operations Area</span>
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${opsAreaControlsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {opsAreaControlsOpen && (
              <section
                id="operations-area-display-controls"
                className="w-full rounded-xl border border-slate-700/80 bg-slate-950/95 p-4 text-xs text-slate-300 shadow-2xl backdrop-blur"
                aria-label="Operations Area display settings"
                data-testid="operations-area-display-controls"
              >
                <div className="mb-3">
                  <h3 className="font-bold text-slate-100">Map display</h3>
                  <p className="mt-1 text-[11px] text-slate-400">
                    The map is focused on the configured country and watch radius. These controls only change your view.
                  </p>
                </div>

                {!opsArea && (
                  <p className="mb-3 rounded-lg border border-slate-800 bg-slate-900/80 px-3 py-2 text-[11px] text-slate-400">
                    No Operations Area is configured for this institution.
                  </p>
                )}

                <label className="flex cursor-pointer items-center justify-between gap-3 py-2 font-medium text-slate-200">
                  <span>Show country highlight</span>
                  <input
                    type="checkbox"
                    checked={opsAreaDisplay.showCountryHighlight}
                    disabled={!opsArea}
                    onChange={(event) => updateOpsAreaDisplay({ showCountryHighlight: event.target.checked })}
                    aria-label="Show Operations Area country highlight"
                    className="h-4 w-4 accent-emerald-600"
                    data-testid="operations-area-country-toggle"
                  />
                </label>

                <div className="border-y border-slate-800 py-3">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label htmlFor="operations-area-highlight-intensity" className="font-medium text-slate-200">
                      Highlight intensity
                    </label>
                    <output htmlFor="operations-area-highlight-intensity" className="font-semibold text-emerald-400">
                      {opsAreaDisplay.highlightIntensity}%
                    </output>
                  </div>
                  <input
                    id="operations-area-highlight-intensity"
                    type="range"
                    min="10"
                    max="60"
                    step="5"
                    value={opsAreaDisplay.highlightIntensity}
                    disabled={!opsArea || !opsAreaDisplay.showCountryHighlight}
                    onChange={(event) => updateOpsAreaDisplay({ highlightIntensity: Number(event.target.value) })}
                    aria-label="Operations Area country highlight intensity"
                    className="w-full accent-emerald-600 disabled:opacity-40"
                    data-testid="operations-area-intensity-slider"
                  />
                  <div className="mt-1 flex justify-between text-[10px] text-slate-400" aria-hidden="true">
                    <span>Lighter</span>
                    <span>Stronger</span>
                  </div>
                </div>

                <label className="flex cursor-pointer items-center justify-between gap-3 py-3 font-medium text-slate-200">
                  <span>Show watch radius</span>
                  <input
                    type="checkbox"
                    checked={opsAreaDisplay.showWatchRadius}
                    disabled={!opsArea}
                    onChange={(event) => updateOpsAreaDisplay({ showWatchRadius: event.target.checked })}
                    aria-label="Show Operations Area watch radius"
                    className="h-4 w-4 accent-emerald-600"
                    data-testid="operations-area-radius-toggle"
                  />
                </label>

                <button
                  type="button"
                  onClick={resetOpsAreaDisplay}
                  className="w-full rounded-lg border border-slate-700 bg-slate-900/80 px-3 py-2 font-semibold text-slate-300 transition hover:border-emerald-800 hover:bg-slate-800 hover:text-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="operations-area-display-reset"
                >
                  Reset display defaults
                </button>
              </section>
            )}

            {foresightArtifactsEnabled && (
              <button
                type="button"
                onClick={() => setShowWarnings((visible) => !visible)}
                className={`flex h-10 w-full items-center justify-between gap-2 rounded-xl border px-3 text-xs font-bold shadow-lg backdrop-blur transition focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${showWarnings
                  ? 'border-amber-700/80 bg-amber-500/10 text-amber-200 hover:bg-amber-500/15'
                  : 'border-slate-700 bg-slate-950/90 text-slate-400 hover:border-amber-800 hover:bg-slate-900 hover:text-amber-300'}`}
                data-testid="warnings-layer-toggle"
                aria-pressed={showWarnings}
                aria-label={`Early warnings (${mapWarnings.length}) — ${showWarnings ? 'visible' : 'hidden'}`}
                title={showWarnings ? 'Hide early warnings on the map' : 'Show early warnings on the map'}
              >
                <span className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
                  <span>Early warnings ({mapWarnings.length})</span>
                </span>
                <span
                  className={`h-2 w-2 rounded-full ${showWarnings
                    ? 'bg-amber-500 shadow-[0_0_0_3px_rgba(245,158,11,0.18)]'
                    : 'bg-slate-300'}`}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
        </div>

        <div className="absolute bottom-3 right-3 z-30 lg:bottom-auto lg:top-3">
          <OperationsSummaryCard
            summary={mapSummary}
            collapsed={operationsSummaryCollapsed}
            expanded={mapExpanded}
            onToggle={() => setOperationsSummaryCollapsed((value) => !value)}
            onToggleExpanded={() => setMapExpanded((value) => !value)}
            onFocusOperationsArea={focusConfiguredOperationsArea}
            operationsAreaInfo={opsArea ? {
              countryName: opsArea.country_name || 'Selected country',
              boundaryLabel: !opsAreaDisplay.showCountryHighlight
                ? 'Country highlight hidden'
                : opsAreaBoundary
                  ? `Country highlighted at ${opsAreaDisplay.highlightIntensity}%`
                  : 'Country boundary unavailable',
              radiusLabel: opsAreaDisplay.showWatchRadius
                ? `${Number(opsArea.radius_km || 0).toLocaleString()} km watch radius visible`
                : 'Watch radius hidden',
              attribution: opsAreaBoundaryAttribution?.text || '',
            } : null}
            persistenceControl={dbMapFocus ? (
              <>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-emerald-400">Map persistence</p>
                <GisSaveControl saveStatus={persistentGis.saveStatus} onSave={persistentGis.saveNow} />
                <p className="mt-2 text-[10px] text-emerald-300">Saved to this {focusedIncident?.is_scenario ? 'scenario' : 'drill'}. Auto-saves as you edit.</p>
              </>
            ) : null}
          />
        </div>

        {scenarioFocus && (
          <div className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-3 rounded-full border border-sky-300/20 bg-slate-950/95 px-4 py-2 text-xs font-semibold text-white shadow-xl backdrop-blur" data-testid="map-scenario-banner">
            <span>Scenario view: {focusedIncident?.incident_type || focusedIncident?.title || focusId}{focusedIncidentWithCoords?.zone_name ? ` · ${focusedIncidentWithCoords.zone_name}` : ''}</span>
            <button onClick={() => navigate('/map')} className="underline hover:no-underline" data-testid="map-scenario-exit">Show full map</button>
          </div>
        )}
        {mapsUnavailable ? (
          <MapUnavailable center={defaultCenter} label="Live operations map" />
        ) : isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={defaultCenter}
            zoom={14}
            onLoad={setMap}
            onClick={handleGisMapClick}
            options={{ ...mapOptions, draggableCursor: (gisAddMode || gisPointMode) ? 'crosshair' : undefined }}
          >
            {layers.links && relationshipLines.map((line) => (
              <Polyline
                key={line.id}
                path={line.path}
                onClick={() => {
                  // Open both incident cards: the root and the related one.
                  setLinkCardIds({ parent: line.parentId, child: line.childId });
                }}
                options={{
                  strokeColor: line.highlighted ? '#f97316' : '#0284c7',
                  strokeOpacity: 0,
                  // The invisible stroke is the click target — keep it wide
                  // so the dotted line is easy to hit; the visible dashes
                  // come from the repeated icons below.
                  strokeWeight: 12,
                  geodesic: true,
                  zIndex: line.highlighted ? 80 : 40,
                  clickable: true,
                  icons: getRelationshipDashIcons(line.highlighted),
                }}
              />
            ))}

            {layers.incidents && (
              <MarkerClusterer
                options={{
                  imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m',
                  gridSize: 60,
                  minimumClusterSize: 2,
                }}
              >
                {(clusterer) => (
                  <>
                    {markersToShow.map((inc) => {
                      const parentId = incidentRelations.parentByChild.get(inc.id);
                      const parentIncident = parentId ? incidentById.get(parentId) : null;
                      const childIncidents = incidentRelations.childrenByParent.get(inc.id) || [];
                      const idQuery = incidentIdSearch.trim().toLowerCase();
                      const isIdSearchMatch = idQuery && matchIncidentIdQuery(inc, idQuery);
                      return (
                        <MapMarker
                          key={inc.id}
                          type="incident"
                          data={inc}
                          isSelected={inc.id === focusId || inc.id === selectedIncidentId}
                          isSearchHighlighted={isIdSearchMatch}
                          isRelatedHighlight={highlightedRelatedIds.has(inc.id)}
                          openCard={linkCardIds?.parent === inc.id || linkCardIds?.child === inc.id}
                          onInfoClose={() =>
                            setLinkCardIds((prev) =>
                              prev && (prev.parent === inc.id || prev.child === inc.id) ? null : prev
                            )
                          }
                          parentIncident={parentIncident}
                          childIncidents={childIncidents}
                          onFocusIncident={focusIncidentOnMap}
                          onClick={(data) => setSelectedIncidentId(data.id)}
                          clusterer={clusterer}
                          departmentNameMap={departmentNameMap}
                        />
                      );
                    })}
                  </>
                )}
              </MarkerClusterer>
            )}

            {layers.teams && filteredTeamLocations.map((team) => (
              <LiveUserMarker
                key={getLocationKey(team)}
                data={team}
                departmentNameMap={departmentNameMap}
                isSelected={
                  String(team.user_id) === String(selectedUserId) ||
                  String(team.user_id) === String(focusUserId)
                }
              />
            ))}

            {userLocation && window.google && (
              <Marker
                position={userLocation}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  fillColor: '#3b82f6',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeWeight: 2,
                  scale: 8,
                }}
                title="Your Location"
                zIndex={1000}
              />
            )}

            {/* ForeSight detection markers use operational scope, not severity:
                red = inside the Operations Area country; orange = external. */}
            {foresightArtifactsEnabled && showWarnings && mapWarnings.map((w) => {
              const c = isInternalForesightWarning(w)
                ? { bg: 'bg-red-500', border: 'border-red-500' }
                : { bg: 'bg-orange-500', border: 'border-orange-500' };
              const isFocused = String(w.id) === String(focusWarningId || '');
              return (
                <OverlayView
                  key={`ew-${w.id}`}
                  position={{ lat: Number(w.gps.latitude), lng: Number(w.gps.longitude) }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    role="button"
                    data-testid={`map-warning-${w.id}`}
                    data-operational-scope={isInternalForesightWarning(w) ? 'internal' : 'external'}
                    aria-current={isFocused ? 'location' : undefined}
                    title={`⚠️ ${w.title} (${w.source_name})`}
                    onClick={() => setWarningPreview(warningToEvent(w))}
                    style={{ transform: 'translate(-50%, -50%)' }}
                    className="relative h-6 w-6 cursor-pointer"
                  >
                    {isFocused && (
                      <span
                        data-testid={`map-warning-focus-${w.id}`}
                        className={`absolute -inset-2 rounded-full border-2 ${c.border} opacity-90`}
                      />
                    )}
                    {/* radar sweep */}
                    <span className={`absolute inset-0 rounded-full border-2 ${c.border} opacity-50 animate-ping`} />
                    {/* thin targeting ring */}
                    <span className={`absolute inset-0 rounded-full border ${c.border} opacity-80`} />
                    {/* solid core */}
                    <span className={`absolute inset-[7px] rounded-full ${c.bg} shadow-[0_0_6px_1px_rgba(0,0,0,0.25)]`} />
                  </div>
                </OverlayView>
              );
            })}

            {/* External feed events — diamond pins colored by severity */}
            {foresightArtifactsEnabled && showExternalEvents && externalEvents.map((ev) => (
              <Marker
                key={`ext-${ev.external_id}`}
                position={{ lat: Number(ev.gps.latitude), lng: Number(ev.gps.longitude) }}
                icon={{
                  path: 'M 0,-10 L 8,0 L 0,10 L -8,0 Z',
                  fillColor: ev.severity === 'High' ? '#e11d48' : ev.severity === 'Medium' ? '#f97316' : '#eab308',
                  fillOpacity: 0.9,
                  strokeColor: '#ffffff',
                  strokeWeight: 1.5,
                  scale: 1,
                }}
                title={`${ev.title} — ${ev.connection?.name || 'external feed'}`}
                zIndex={60}
                onClick={() => setSelectedExternalId(ev.external_id)}
              >
                {selectedExternalId === ev.external_id && (
                  <InfoWindow onCloseClick={() => setSelectedExternalId(null)}>
                    <div style={{ maxWidth: 240 }}>
                      <p className="text-xs font-bold text-gray-900 mb-1">{ev.title}</p>
                      <p className="text-[11px] text-gray-600 mb-1.5">
                        {ev.connection?.name} · {ev.severity} · {distanceLabel(ev)}
                      </p>
                      <button
                        type="button"
                        className="rounded bg-sky-700 px-2 py-1 text-[11px] font-bold text-white hover:bg-sky-800"
                        onClick={() => { setSelectedExternalId(null); setWarningPreview(ev); }}
                      >
                        Dispatch warning
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            ))}

            <MapGisOverlay
              tasks={gisTasksToShow} setTasks={setActiveTasks}
              zones={gisZonesToShow} setZones={setActiveZones}
              points={gisPointsToShow} setPoints={setActivePoints}
              onEditPoint={(p) => setGisPointDraft(p)}
              drawShape={gisDrawShape} setDrawShape={setGisDrawShape}
              heatmapOn={activeHeatmap} sequenceOn={activeSequence}
              activeImportance={activeImportance}
              selectedTaskId={gisSelectedTaskId} setSelectedTaskId={setGisSelectedTaskId}
              onEditTask={(t) => { setGisDraft(t); setGisSelectedTaskId(null); }}
              incidents={markersToShow}
              incidentId={focusId || undefined}
            />

          </GoogleMap>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Loading map…</div>
        )}
        <TaskFormModal draft={gisDraft} onSave={saveGisTask} onClose={() => setGisDraft(null)} actionCards={gisActionCards} />
        <PointFormModal draft={gisPointDraft} onSave={saveGisPoint} onClose={() => setGisPointDraft(null)} />
        {foresightArtifactsEnabled && warningPreview && (
          <EventPreviewModal
            event={warningPreview}
            onClose={() => setWarningPreview(null)}
            onScenario={buildScenarioFromWarning}
            onDismissed={(id) => setMapWarnings((prev) => prev.filter((w) => w.id !== id))}
          />
        )}
      </main>
    </div>
  );
};

export default MapPage;
